import { useState, useEffect, useCallback } from "react"
import scriptjs from "scriptjs"
import { SPEEDOFME_ACCOUNT_KEY, SPEEDOFME_DOMAIN_NAME } from "@/globalConfig"

const DEFAULT_SCRIPT_URL = "//speedof.me/api/api.js"

export interface SOMResponse {
  latency: number
  jitter: number
  download: number
  upload: number
  maxDownload: number
  maxUpload: number
  testServer: string
  ip_address: string
  hostname: string
  userAgent: string
  testDate: string
}

interface SOMProgress {
  type: string
  pass: string
  percentDone: number
  currentSpeed: number
}

interface SOMError {
  code: number
  message: string
}

const useSpeedTest = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<SOMResponse | null>(null)
  const [error, setError] = useState<SOMError | null>(null)
  const [progress, setProgress] = useState<SOMProgress | null>(null)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  useEffect(() => {
    scriptjs(DEFAULT_SCRIPT_URL, () => {
      if (typeof (window as any).SomApi !== "undefined") {
        const SomApi = (window as any).SomApi
        SomApi.account = SPEEDOFME_ACCOUNT_KEY
        SomApi.domainName = SPEEDOFME_DOMAIN_NAME
        SomApi.onTestCompleted = handleTestCompleted
        SomApi.onError = handleTestError
        SomApi.onProgress = handleProgress
        setIsScriptLoaded(true)
      } else {
        console.error("Make sure the SpeedOf.Me API script is loaded")
      }
    })
  }, [])

  const handleTestCompleted = (result: SOMResponse) => {
    setResult(result)
    setIsLoading(false)
  }

  const handleTestError = (error: SOMError) => {
    setError(error)
    setIsLoading(false)
  }

  const handleProgress = (progress: SOMProgress) => {
    setProgress(progress)
  }

  const startTest = useCallback(async () => {
    const SomApi = (window as any).SomApi
    if (SomApi !== "undefined") {
      setIsLoading(true)
      await SomApi.startTest()
    }
  }, [])

  return {
    isLoading,
    result,
    error,
    progress,
    startTest,
    isScriptLoaded,
  }
}

export default useSpeedTest
